import React, { useState, useEffect } from 'react';
import { API } from '@/lib/api-client';
import Toast, { ToastType } from '@/components/ui/Toast';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { UserRoleManagement } from './UserRoleManagement';
import { integrationService, EmployeeData, PositionData } from '@/services/api/integrationService';
import organizationService from '@/services/api/organizationService';
import {
  Users,
  UserPlus,
  Shield,
  Search,
  Filter,
  Download,
  Upload,
  MoreVertical,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  X,
  Building2,
  Briefcase,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Hash,
  User as UserIcon,
  Loader2,
  Globe,
  Linkedin,
  Home,
  CreditCard,
  Key,
  GraduationCap,
  UserX,
  Users2
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSyncHistory } from '@/hooks/useSyncHistory';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  employee_id: string | null;
  campus: string | null;
  department: string | null;
  functional_area: string | null;
  job_title: string | null;
  manager_id: string | null;
  is_active: boolean;
  roles: string[];
  last_login?: string;
}

// ReportsTo editing is gated by environment — PROD relies on F&O sync as the source of truth.
const IS_PROD_ENV = ['production', 'prod'].includes(
  (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase()
);

const UserManagement: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [filterRole, setFilterRole] = useState<string>('');

  const { lastSync, refetch: refetchHistory } = useSyncHistory({ limit: 1, entity_type: 'employees' });
  const lastSyncTime = lastSync?.sync_started_at
    ? new Date(lastSync.sync_started_at).toLocaleString()
    : null;
  const [filterCampus, setFilterCampus] = useState<string>('');
  const [filterDepartment, setFilterDepartment] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, employed: 0, terminated: 0, thisMonth: 0 });
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState<User | null>(null);
  const [selectedUserForReset, setSelectedUserForReset] = useState<User | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // F&O Worker Details state
  const [selectedUserForFO, setSelectedUserForFO] = useState<User | null>(null);
  const [showFODialog, setShowFODialog] = useState(false);
  const [foWorkerData, setFOWorkerData] = useState<EmployeeData | null>(null);
  const [foPositionData, setFOPositionData] = useState<PositionData | null>(null);
  const [isFetchingFO, setIsFetchingFO] = useState(false);
  const [foError, setFOError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  // Fetch users when filters or pagination changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, searchQuery, filterCampus, filterDepartment, filterStatus, filterRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (filterCampus) params.campus = filterCampus;
      if (filterDepartment) params.department = filterDepartment;
      if (filterRole) params.role = filterRole;
      if (filterStatus !== 'all') {
        params.is_active = filterStatus === 'active';
      }
      
      const data = await API.users.list(params);
      
      setUsers(data.users);
      setTotalUsers(data.total);
      setTotalPages(data.total_pages);
      
      // Update stats
      if (currentPage === 1 && !searchQuery && !filterCampus && !filterDepartment && !filterRole && filterStatus === 'all') {
        setStats({
          total: data.total,
          active: data.total,
          employed: data.total, // Will be updated by a separate stats call if needed
          terminated: 0,
          thisMonth: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load users';
      showToast(
        errorMessage.includes('401') || errorMessage.includes('403') 
          ? 'Authentication required. Please log in.'
          : 'Failed to load users. Please try again.',
        'error'
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type });
  };

  const handleSync = async () => {
    setShowSyncDialog(false);
    setIsSyncing(true);
    try {
      const results = await organizationService.syncFromFO();

      const empResult = results.employees;
      const posResult = results.positions;
      const totalSynced = empResult.successful_records + posResult.successful_records;

      showToast(
        `Successfully synced ${totalSynced} records (${empResult.successful_records} team members, ${posResult.successful_records} positions)`,
        'success'
      );

      // Refresh users and sync history after sync
      await Promise.all([fetchUsers(), refetchHistory()]);
    } catch (error) {
      console.error('Sync failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sync data from F&O';
      showToast(
        errorMessage.includes('401') || errorMessage.includes('403')
          ? 'Authentication required to sync. Please log in.'
          : errorMessage,
        'error'
      );
      await refetchHistory();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleViewFODetails = async (user: User) => {
    if (!user.employee_id) {
      showToast('No employee ID linked to this user', 'error');
      return;
    }
    
    setSelectedUserForFO(user);
    setShowFODialog(true);
    setFOWorkerData(null);
    setFOPositionData(null);
    setFOError(null);
    setIsFetchingFO(true);
    
    try {
      // Fetch full worker details from F&O
      const employeeData = await integrationService.getEmployeeByPersonnelNumber(user.employee_id);
      setFOWorkerData(employeeData);
      
      // Also try to get position data if available
      try {
        const { positions } = await integrationService.getAllPositions({ top: 500 });
        const userPosition = positions.find(p => p.worker_personnel_number === user.employee_id);
        if (userPosition) {
          setFOPositionData(userPosition);
        }
      } catch (posErr) {
        console.warn('Could not fetch position data:', posErr);
      }
    } catch (error) {
      console.error('Failed to fetch F&O worker data:', error);
      setFOError(error instanceof Error ? error.message : 'Failed to fetch F&O data');
    } finally {
      setIsFetchingFO(false);
    }
  };

  const handleResetOnboarding = async () => {
    if (!selectedUserForReset) return;
    
    setShowResetDialog(false);
    setIsResetting(true);
    try {
      await API.users.resetOnboarding(selectedUserForReset.id);
      
      showToast(
        `Onboarding status reset successfully for ${selectedUserForReset.display_name}. They will see the onboarding wizard on next login.`,
        'success'
      );
      
      setSelectedUserForReset(null);
    } catch (error) {
      console.error('Reset onboarding failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset onboarding status';
      showToast(
        errorMessage.includes('401') || errorMessage.includes('403')
          ? 'Authentication required. Please log in.'
          : `Failed to reset onboarding: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsResetting(false);
    }
  };

  // Deactivate user
  const [selectedUserForDeactivate, setSelectedUserForDeactivate] = useState<User | null>(null);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Edit ReportsTo (UAT/dev only — PROD hides this control)
  const [selectedUserForManager, setSelectedUserForManager] = useState<User | null>(null);
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [currentManagerDisplay, setCurrentManagerDisplay] = useState<string | null>(null);
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [managerSearchResults, setManagerSearchResults] = useState<User[]>([]);
  const [isSearchingManagers, setIsSearchingManagers] = useState(false);
  const [pickedManager, setPickedManager] = useState<User | null>(null);
  const [isSavingManager, setIsSavingManager] = useState(false);

  const handleDeactivateUser = async () => {
    if (!selectedUserForDeactivate) return;

    setShowDeactivateDialog(false);
    setIsDeactivating(true);
    try {
      await API.users.deactivate(selectedUserForDeactivate.id);
      showToast(
        `${selectedUserForDeactivate.display_name} has been deactivated successfully.`,
        'success'
      );
      setSelectedUserForDeactivate(null);
      await fetchUsers();
    } catch (error) {
      console.error('Deactivate user failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to deactivate user';
      showToast(
        errorMessage.includes('401') || errorMessage.includes('403')
          ? 'You do not have permission to deactivate users.'
          : `Failed to deactivate user: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsDeactivating(false);
    }
  };

  // Open the "Edit Reports To" dialog and fetch current manager display name.
  const openManagerDialog = async (user: User) => {
    setSelectedUserForManager(user);
    setShowManagerDialog(true);
    setManagerSearchQuery('');
    setManagerSearchResults([]);
    setPickedManager(null);
    setCurrentManagerDisplay(null);

    if (user.manager_id) {
      try {
        const mgr = await API.users.get(user.manager_id);
        if (mgr) {
          setCurrentManagerDisplay(
            `${mgr.display_name || `${mgr.first_name || ''} ${mgr.last_name || ''}`.trim()}${mgr.email ? ` (${mgr.email})` : ''}`
          );
        }
      } catch (err) {
        console.warn('Failed to fetch current manager', err);
        setCurrentManagerDisplay(`User #${user.manager_id}`);
      }
    }
  };

  // Debounced search for manager candidates
  useEffect(() => {
    if (!showManagerDialog) return;
    const trimmed = managerSearchQuery.trim();
    if (trimmed.length < 2) {
      setManagerSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingManagers(true);
        const data = await API.users.list({
          search: trimmed,
          page: 1,
          page_size: 20,
          is_active: true,
        });
        const filtered = (data.users || []).filter(
          (u: User) => selectedUserForManager ? u.id !== selectedUserForManager.id : true
        );
        setManagerSearchResults(filtered);
      } catch (err) {
        console.error('Manager search failed', err);
        setManagerSearchResults([]);
      } finally {
        setIsSearchingManagers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [managerSearchQuery, showManagerDialog, selectedUserForManager]);

  const handleSaveManager = async (newManagerId: string | null) => {
    if (!selectedUserForManager) return;
    setIsSavingManager(true);
    try {
      await API.users.updateManager(selectedUserForManager.id, newManagerId);
      showToast(
        newManagerId
          ? `Reports To updated for ${selectedUserForManager.display_name}. Next F&O sync will overwrite this.`
          : `Reports To cleared for ${selectedUserForManager.display_name}. Next F&O sync will overwrite this.`,
        'success'
      );
      setShowManagerDialog(false);
      setSelectedUserForManager(null);
      await fetchUsers();
    } catch (error) {
      console.error('Failed to update people leader', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update people leader';
      showToast(
        errorMessage.includes('403')
          ? 'People Leader updates are disabled in this environment.'
          : `Failed to update people leader: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsSavingManager(false);
    }
  };

  // Role options for filtering - derived from actual system roles

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">
            Manage user accounts, roles, and permissions across all campuses
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button className="bg-heritage-blue hover:bg-heritage-blue/90">
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.total.toLocaleString(), icon: Users, color: 'text-heritage-blue' },
          { label: 'Employed', value: stats.employed.toLocaleString(), icon: CheckCircle2, color: 'text-emerald-600' },
          { label: 'Terminated', value: stats.terminated.toString(), icon: XCircle, color: 'text-gray-600' },
          { label: 'New This Month', value: stats.thisMonth.toString(), icon: UserPlus, color: 'text-purple-600' }
        ].map((stat, idx) => (
          <Card key={idx} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-opacity-10 ${stat.color}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, personnel ID..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'More Filters'}
              </Button>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
          
          {/* Extended Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Users</option>
                  <option value="active">Employed Only</option>
                  <option value="inactive">Terminated Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campus</label>
                <Input
                  placeholder="Filter by campus"
                  value={filterCampus}
                  onChange={(e) => {
                    setFilterCampus(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Function</label>
                <Input
                  placeholder="Filter by function"
                  value={filterDepartment}
                  onChange={(e) => {
                    setFilterDepartment(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                  value={filterRole}
                  onChange={(e) => {
                    setFilterRole(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Roles</option>
                  <option value="admin">Administrator</option>
                  <option value="hr_admin">HR Administrator</option>
                  <option value="hrbp">HRBP</option>
                  <option value="fno_admin">F&O Administrator</option>
                  <option value="data_team">Data Quality Team</option>
                  <option value="payroll_ops">Payroll Operations</option>
                  <option value="manager">People Leader</option>
                  <option value="employee">Team Member</option>
                </select>
              </div>
              
              <div className="md:col-span-4 flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setFilterStatus('all');
                    setFilterCampus('');
                    setFilterDepartment('');
                    setFilterRole('');
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* User List */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-heritage-blue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider min-w-[150px] hidden md:table-cell">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider min-w-[120px] hidden lg:table-cell">
                    Campus
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider min-w-[100px] hidden xl:table-cell">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider min-w-[120px] hidden xl:table-cell">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-heritage-blue rounded-full flex items-center justify-center text-white font-semibold">
                        {user.first_name?.[0] || 'U'}{user.last_name?.[0] || 'U'}
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{user.display_name}</div>
                        <div className="text-sm text-gray-500 truncate">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <div className="text-sm text-gray-900 truncate">{user.job_title || 'N/A'}</div>
                    <div className="text-xs text-gray-500 truncate">{user.roles.join(', ') || 'No roles'}</div>
                  </td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <div className="text-sm text-gray-900 truncate">{user.campus || 'N/A'}</div>
                    <div className="text-xs text-gray-500 truncate">{user.functional_area || user.department || ''}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap hidden xl:table-cell">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-medium ${user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                      {user.is_active ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {user.is_active ? 'Employed' : 'Terminated'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                    {user.last_login 
                      ? new Date(user.last_login).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
                      : 'Never'
                    }
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {user.employee_id && (
                            <DropdownMenuItem
                              onClick={() => handleViewFODetails(user)}
                              className="cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2 text-amber-600" />
                              View F&O Details
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => setSelectedUserForRoles(user)}
                            className="cursor-pointer"
                          >
                            <Shield className="w-4 h-4 mr-2 text-heritage-blue" />
                            Manage Roles
                          </DropdownMenuItem>
                          {!IS_PROD_ENV && (
                            <DropdownMenuItem
                              onClick={() => openManagerDialog(user)}
                              className="cursor-pointer"
                            >
                              <Users2 className="w-4 h-4 mr-2 text-indigo-600" />
                              Edit Reports To
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUserForReset(user);
                              setShowResetDialog(true);
                            }}
                            disabled={isResetting}
                            className="cursor-pointer"
                          >
                            <RefreshCw className="w-4 h-4 mr-2 text-purple-600" />
                            Reset Onboarding
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user.is_active ? (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUserForDeactivate(user);
                                setShowDeactivateDialog(true);
                              }}
                              disabled={isDeactivating}
                              className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Deactivate User
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              disabled
                              className="text-gray-400"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Already Inactive
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {!loading && users.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalUsers)}</span> of{' '}
                    <span className="font-medium">{totalUsers.toLocaleString()}</span> users
                  </span>
                  <select
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10 per page</option>
                    <option value={30}>30 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* F&O Sync Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-heritage-blue/10 rounded-lg">
              <Users className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">F&O Data Sync</h3>
              <p className="text-sm text-gray-600 mt-1">
                Synchronize team member data from Dynamics Finance & Operations
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {lastSyncTime ? `Last synced: ${lastSyncTime}` : 'Never synced'}
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => setShowSyncDialog(true)}
            disabled={isSyncing}
          >
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </Card>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Sync Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        onConfirm={handleSync}
        title="Sync F&O Data"
        message={`This will synchronize all team member data from Dynamics F&O. The process may take 2-3 minutes.

Do you want to proceed?`}
        confirmText="Start Sync"
        type="info"
      />

      {/* Reset Onboarding Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetDialog}
        onClose={() => {
          setShowResetDialog(false);
          setSelectedUserForReset(null);
        }}
        onConfirm={handleResetOnboarding}
        title="Reset Onboarding Status"
        message={selectedUserForReset ? `Are you sure you want to reset the onboarding status for ${selectedUserForReset.display_name}?

This will:
• Clear their onboarding completion status
• Clear all onboarding step progress
• Show them the onboarding wizard on their next login

This is useful when a user needs to re-complete onboarding due to data issues or if they skipped important steps.` : ''}
        confirmText="Reset Onboarding"
        type="warning"
      />

      {/* Deactivate User Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeactivateDialog}
        onClose={() => {
          setShowDeactivateDialog(false);
          setSelectedUserForDeactivate(null);
        }}
        onConfirm={handleDeactivateUser}
        title="Deactivate User"
        message={selectedUserForDeactivate ? `Are you sure you want to deactivate ${selectedUserForDeactivate.display_name} (${selectedUserForDeactivate.email})?

This will:
• Set the user as inactive
• They will no longer appear in active user lists
• Their data will be preserved but they won't be able to use the platform

This action can be reversed by reactivating the user.` : ''}
        confirmText="Deactivate"
        type="danger"
      />

      {/* Edit Reports To Dialog (UAT/dev only) */}
      {showManagerDialog && selectedUserForManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              if (!isSavingManager) {
                setShowManagerDialog(false);
                setSelectedUserForManager(null);
              }
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Users2 className="w-5 h-5 text-indigo-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Edit Reports To</h2>
                  <p className="text-sm text-gray-600">
                    {selectedUserForManager.display_name}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowManagerDialog(false);
                  setSelectedUserForManager(null);
                }}
                disabled={isSavingManager}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                Changes here are local to the platform DB and will be overwritten by the next F&O daily sync.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current People Leader</label>
                <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded border border-gray-200">
                  {currentManagerDisplay || <span className="text-gray-400 italic">None</span>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search New People Leader</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Type at least 2 characters..."
                    className="pl-10"
                    value={managerSearchQuery}
                    onChange={(e) => setManagerSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                {isSearchingManagers ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                  </div>
                ) : managerSearchResults.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {managerSearchQuery.trim().length < 2
                      ? 'Start typing to search'
                      : 'No matching users'}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {managerSearchResults.map((u) => {
                      const isPicked = pickedManager?.id === u.id;
                      return (
                        <li key={u.id}>
                          <button
                            type="button"
                            onClick={() => setPickedManager(u)}
                            className={`w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between ${isPicked ? 'bg-indigo-50' : ''}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">{u.display_name}</div>
                              <div className="text-xs text-gray-500 truncate">{u.email}{u.employee_id ? ` · ${u.employee_id}` : ''}</div>
                            </div>
                            {isPicked && <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {pickedManager && (
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm">
                  <span className="text-gray-600">New people leader:</span>{' '}
                  <span className="font-medium text-gray-900">{pickedManager.display_name}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => handleSaveManager(null)}
                disabled={isSavingManager || !selectedUserForManager.manager_id}
                className="text-red-600 hover:text-red-700"
              >
                Clear People Leader
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowManagerDialog(false);
                    setSelectedUserForManager(null);
                  }}
                  disabled={isSavingManager}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => pickedManager && handleSaveManager(pickedManager.id)}
                  disabled={isSavingManager || !pickedManager}
                  className="bg-heritage-blue hover:bg-heritage-blue/90"
                >
                  {isSavingManager ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Role Management Modal */}
      {selectedUserForRoles && (
        <UserRoleManagement
          userId={selectedUserForRoles.id}
          userEmail={selectedUserForRoles.email}
          userName={selectedUserForRoles.display_name}
          onClose={() => setSelectedUserForRoles(null)}
          onRoleChanged={() => {
            fetchUsers();
            setToast({ message: 'User roles updated successfully', type: 'success' });
          }}
        />
      )}

      {/* F&O Worker Details Dialog */}
      {showFODialog && selectedUserForFO && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => {
              setShowFODialog(false);
              setSelectedUserForFO(null);
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">F&O Worker Details</h2>
                  <p className="text-sm text-gray-600">
                    {selectedUserForFO.display_name} ({selectedUserForFO.employee_id})
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowFODialog(false);
                  setSelectedUserForFO(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {isFetchingFO ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-amber-600 animate-spin mb-3" />
                  <p className="text-gray-600">Fetching F&O worker data...</p>
                </div>
              ) : foError ? (
                <div className="space-y-6">
                  {/* Error Banner */}
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <XCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-amber-800 font-medium">F&O API Unavailable</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Could not fetch live data from Dynamics F&O. Showing synced data from platform database.
                      </p>
                      <p className="text-amber-600 text-xs mt-2">{foError}</p>
                    </div>
                  </div>
                  
                  {/* Show synced user data as fallback */}
                  {selectedUserForFO && (
                    <>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <UserIcon className="w-4 h-4" /> Synced User Data
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <DetailField label="Personnel Number" value={selectedUserForFO.employee_id} icon={<Hash className="w-4 h-4" />} highlight />
                          <DetailField label="Display Name" value={selectedUserForFO.display_name} />
                          <DetailField label="First Name" value={selectedUserForFO.first_name} />
                          <DetailField label="Last Name" value={selectedUserForFO.last_name} />
                          <DetailField label="Email" value={selectedUserForFO.email} icon={<Mail className="w-4 h-4" />} />
                          <DetailField label="Job Title" value={selectedUserForFO.job_title} icon={<Briefcase className="w-4 h-4" />} />
                          <DetailField label="Function" value={selectedUserForFO.functional_area || selectedUserForFO.department} />
                          <DetailField label="Campus" value={selectedUserForFO.campus} icon={<MapPin className="w-4 h-4" />} />
                          <DetailField 
                            label="Status" 
                            value={selectedUserForFO.is_active ? 'Employed' : 'Terminated'} 
                            valueColor={selectedUserForFO.is_active ? 'text-emerald-600' : 'text-gray-600'}
                          />
                          <DetailField label="Platform Roles" value={selectedUserForFO.roles?.join(', ') || 'None'} />
                          <DetailField 
                            label="Last Login" 
                            value={selectedUserForFO.last_login ? new Date(selectedUserForFO.last_login).toLocaleString('en-ZA') : 'Never'}
                            icon={<Calendar className="w-4 h-4" />}
                          />
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Note:</strong> This data was synced from F&O during the last team member sync. 
                          To view full F&O worker details (addresses, personal info, Sage codes), ensure the F&O sandbox is accessible and try again.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : foWorkerData ? (
                <div className="space-y-6">
                  {/* Worker Identity Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <UserIcon className="w-4 h-4" /> Worker Identity
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField label="Personnel Number" value={foWorkerData.personnel_number} icon={<Hash className="w-4 h-4" />} highlight />
                      <DetailField label="Party Number" value={foWorkerData.party_number} icon={<Hash className="w-4 h-4" />} />
                      <DetailField label="Full Name" value={foWorkerData.full_name} />
                      <DetailField label="Title" value={foWorkerData.personal_title} />
                      <DetailField label="First Name" value={foWorkerData.first_name} />
                      <DetailField label="Middle Name" value={foWorkerData.middle_name} />
                      <DetailField label="Last Name" value={foWorkerData.last_name} />
                      <DetailField label="Known As" value={foWorkerData.known_as} />
                      <DetailField label="Name Alias" value={foWorkerData.name_alias} />
                      <DetailField label="User Account" value={foWorkerData.user} icon={<Key className="w-4 h-4" />} />
                    </div>
                  </div>

                  {/* Employment Status Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> Employment Status
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField 
                        label="Worker Status" 
                        value={foWorkerData.worker_status} 
                        highlight 
                        valueColor={foWorkerData.worker_status === 'Employed' ? 'text-emerald-600' : 'text-gray-600'}
                      />
                      <DetailField label="Worker Type" value={foWorkerData.worker_type} />
                      <DetailField label="Job Title" value={foWorkerData.title_id} icon={<Briefcase className="w-4 h-4" />} />
                      <DetailField label="Office Location" value={foWorkerData.office_location} icon={<MapPin className="w-4 h-4" />} />
                      <DetailField 
                        label="Original Hire Date" 
                        value={foWorkerData.original_hire_date ? new Date(foWorkerData.original_hire_date).toLocaleDateString('en-ZA') : undefined} 
                        icon={<Calendar className="w-4 h-4" />}
                      />
                      <DetailField 
                        label="Anniversary Date" 
                        value={foWorkerData.anniversary_date ? new Date(foWorkerData.anniversary_date).toLocaleDateString('en-ZA') : undefined} 
                        icon={<Calendar className="w-4 h-4" />}
                      />
                      <DetailField 
                        label="Seniority Date" 
                        value={foWorkerData.seniority_date ? new Date(foWorkerData.seniority_date).toLocaleDateString('en-ZA') : undefined} 
                      />
                      <DetailField label="Works From Home" value={foWorkerData.works_from_home} icon={<Home className="w-4 h-4" />} />
                      <DetailField label="Allow Rehire" value={foWorkerData.allow_rehire} />
                      <DetailField label="Language" value={foWorkerData.language_id} icon={<Globe className="w-4 h-4" />} />
                    </div>
                  </div>

                  {/* EmploymentsV2 Details Section */}
                  {(foWorkerData.employment_campus || foWorkerData.employment_faculty || foWorkerData.employment_start_date) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" /> Employment Details (HR)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <DetailField label="Campus" value={foWorkerData.employment_campus} icon={<MapPin className="w-4 h-4" />} />
                        <DetailField label="Campus Name" value={foWorkerData.employment_campus_name} icon={<MapPin className="w-4 h-4" />} />
                        <DetailField label="Faculty" value={foWorkerData.employment_faculty} icon={<GraduationCap className="w-4 h-4" />} />
                        <DetailField label="Faculty Name" value={foWorkerData.employment_faculty_name} />
                        <DetailField label="Grade Level" value={foWorkerData.employment_hed_grade_level_id} />
                        <DetailField label="NQF Level" value={foWorkerData.employment_hcm_nqf_level} />
                        <DetailField label="NQF Description" value={foWorkerData.employment_hcm_nqf_level_description} />
                        <DetailField label="High Potential" value={foWorkerData.employment_high_potential} />
                        <DetailField
                          label="Employment Start"
                          value={foWorkerData.employment_start_date ? new Date(foWorkerData.employment_start_date).toLocaleDateString('en-ZA') : undefined}
                          icon={<Calendar className="w-4 h-4" />}
                        />
                      </div>
                    </div>
                  )}

                  {/* Position & Job Details Section */}
                  {(foWorkerData.position_id || foWorkerData.position_department_name || foWorkerData.position_job_family_id) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Position & Job Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <DetailField label="Position ID" value={foWorkerData.position_id} icon={<Hash className="w-4 h-4" />} />
                        <DetailField label="Position" value={foWorkerData.position_description} icon={<Briefcase className="w-4 h-4" />} />
                        <DetailField label="Position Type" value={foWorkerData.position_type_id} />
                        <DetailField label="Department" value={foWorkerData.position_department_name || foWorkerData.position_department_number} icon={<Building2 className="w-4 h-4" />} />
                        <DetailField label="Job ID" value={foWorkerData.position_job_id} />
                        <DetailField label="Job Title" value={foWorkerData.position_job_title} />
                        <DetailField label="Job Family (Functional Area)" value={foWorkerData.position_job_family_id} icon={<Building2 className="w-4 h-4" />} />
                        <DetailField label="Function ID" value={foWorkerData.position_function_id} />
                        <DetailField label="Reports To Position" value={foWorkerData.position_reports_to_position_id} />
                      </div>
                    </div>
                  )}

                  {/* Contact Information Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField label="Email" value={foWorkerData.primary_contact_email} icon={<Mail className="w-4 h-4" />} />
                      <DetailField label="Email Type" value={foWorkerData.primary_contact_email_description} />
                      <DetailField label="Email Purpose" value={foWorkerData.primary_contact_email_purpose} />
                      <DetailField label="Phone" value={foWorkerData.primary_contact_phone} icon={<Phone className="w-4 h-4" />} />
                      <DetailField label="Phone Type" value={foWorkerData.primary_contact_phone_description} />
                      <DetailField label="Is Mobile" value={foWorkerData.primary_contact_phone_is_mobile} />
                      <DetailField label="Phone Extension" value={foWorkerData.primary_contact_phone_extension} />
                      <DetailField label="LinkedIn" value={foWorkerData.primary_contact_linkedin} icon={<Linkedin className="w-4 h-4" />} />
                      <DetailField label="Website" value={foWorkerData.primary_contact_url} icon={<Globe className="w-4 h-4" />} />
                    </div>
                  </div>

                  {/* Address Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Home className="w-4 h-4" /> Address
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField label="Street" value={foWorkerData.address_street} />
                      <DetailField label="City" value={foWorkerData.address_city} />
                      <DetailField label="State" value={foWorkerData.address_state} />
                      <DetailField label="Zip Code" value={foWorkerData.address_zip_code} />
                      <DetailField label="County" value={foWorkerData.address_county} />
                      <DetailField label="Country" value={foWorkerData.address_country_region_id} />
                      <DetailField label="Country ISO" value={foWorkerData.address_country_region_iso_code} />
                      <DetailField label="Purpose" value={foWorkerData.address_purpose} />
                      <DetailField label="Address Books" value={foWorkerData.address_books} />
                    </div>
                  </div>

                  {/* Personal Information Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <UserIcon className="w-4 h-4" /> Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField label="Gender" value={foWorkerData.gender} />
                      <DetailField 
                        label="Birth Date" 
                        value={foWorkerData.birth_date ? new Date(foWorkerData.birth_date).toLocaleDateString('en-ZA') : undefined}
                        icon={<Calendar className="w-4 h-4" />}
                      />
                      <DetailField label="Birth City" value={foWorkerData.person_birth_city} />
                      <DetailField label="Birth Country" value={foWorkerData.person_birth_country_region} />
                      <DetailField label="Marital Status" value={foWorkerData.marital_status} />
                      <DetailField label="Ethnic Origin" value={foWorkerData.ethnic_origin_id} />
                      <DetailField label="Citizenship" value={foWorkerData.citizenship_country_region} icon={<Globe className="w-4 h-4" />} />
                      <DetailField label="Nationality" value={foWorkerData.nationality_country_region} />
                      <DetailField label="Number of Dependents" value={foWorkerData.number_of_dependents?.toString()} />
                      <DetailField label="Education" value={foWorkerData.education} icon={<GraduationCap className="w-4 h-4" />} />
                      <DetailField label="Native Language" value={foWorkerData.native_language_id} />
                      <DetailField label="Full-time Student" value={foWorkerData.is_fulltime_student} />
                      <DetailField label="Disability Status" value={foWorkerData.is_disabled} />
                      <DetailField label="Veteran Status" value={foWorkerData.veteran_status_id} />
                    </div>
                  </div>

                  {/* Sage Integration Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Sage Integration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField 
                        label="Sage Integrated" 
                        value={foWorkerData.sage_integrated} 
                        highlight
                        valueColor={foWorkerData.sage_integrated === 'Yes' ? 'text-emerald-600' : 'text-gray-600'}
                      />
                      <DetailField label="Sage ID" value={foWorkerData.sage_id} icon={<Hash className="w-4 h-4" />} />
                      <DetailField label="Company Rule Code" value={foWorkerData.sage_company_rule_code} />
                      <DetailField label="Nature of Contract" value={foWorkerData.sage_nature_of_contract_code} />
                      <DetailField label="Email Payslip Code" value={foWorkerData.sage_email_basic_payslip_code} />
                    </div>
                  </div>

                  {/* System Identity Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" /> System Identity
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailField label="Azure AD Object ID" value={foWorkerData.object_id} icon={<Hash className="w-4 h-4" />} />
                      <DetailField label="Identity Email" value={foWorkerData.identity_email} icon={<Mail className="w-4 h-4" />} />
                      <DetailField label="Identity Provider" value={foWorkerData.identity_provider} />
                      <DetailField label="Party Type" value={foWorkerData.party_type} />
                    </div>
                  </div>

                  {/* Position Data Section (if available) */}
                  {foPositionData && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Position Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <DetailField label="Position ID" value={foPositionData.position_id} icon={<Hash className="w-4 h-4" />} highlight />
                        <DetailField label="Position Description" value={foPositionData.position_description} />
                        <DetailField label="Job ID" value={foPositionData.job_id} />
                        <DetailField label="Title ID" value={foPositionData.title_id} />
                        <DetailField label="Department Number" value={foPositionData.department_number} />
                        <DetailField label="Is Primary Position" value={foPositionData.is_primary_position} />
                        <DetailField label="FTE" value={foPositionData.full_time_equivalent?.toString()} />
                        <DetailField label="Reports To Position" value={foPositionData.reports_to_position_id} />
                        <DetailField 
                          label="Detail Effective" 
                          value={foPositionData.detail_effective ? new Date(foPositionData.detail_effective).toLocaleDateString('en-ZA') : undefined}
                        />
                        <DetailField 
                          label="Detail Expiration" 
                          value={foPositionData.detail_expiration ? new Date(foPositionData.detail_expiration).toLocaleDateString('en-ZA') : undefined}
                        />
                        <DetailField 
                          label="Activation" 
                          value={foPositionData.activation ? new Date(foPositionData.activation).toLocaleDateString('en-ZA') : undefined}
                        />
                        <DetailField 
                          label="Retirement" 
                          value={foPositionData.retirement ? new Date(foPositionData.retirement).toLocaleDateString('en-ZA') : undefined}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => {
                  setShowFODialog(false);
                  setSelectedUserForFO(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for displaying detail fields
const DetailField: React.FC<{
  label: string;
  value?: string | null;
  icon?: React.ReactNode;
  highlight?: boolean;
  valueColor?: string;
}> = ({ label, value, icon, highlight, valueColor }) => (
  <div className={`p-3 rounded-lg ${highlight ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
      {icon}
      {label}
    </div>
    <div className={`text-sm font-medium ${valueColor || 'text-gray-900'}`}>
      {value || <span className="text-gray-400 italic">Not set</span>}
    </div>
  </div>
);

export default UserManagement;
